import React, { useEffect, useState } from 'react'
import { getWalletNFTs, type WalletNFTItem } from '../../api/wallet'

export default function NFTGrid({ address }: { address?: string }) {
  const [items, setItems] = useState<WalletNFTItem[]>([])
  const [next, setNext] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lightbox, setLightbox] = useState<{ open: boolean; url: string; title: string }>({ open: false, url: '', title: '' })
  const [failedImageIds, setFailedImageIds] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (!address) return
    setLoading(true)
    setError(null)
    getWalletNFTs()
      .then((p) => { setItems(p.items || []); setNext(p.next || null) })
      .catch((e) => setError(e?.message || 'Failed to load handles'))
      .finally(()=> setLoading(false))
  }, [address])

  async function loadMore() {
    if (!next || loading) return
    setLoading(true)
    try {
      const p = await getWalletNFTs(next)
      setItems((prev) => prev.concat(p.items || []))
      setNext(p.next || null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load more')
    } finally {
      setLoading(false)
    }
  }

  if (!address) {
    return <div className="text-white/70 text-sm">Connect your wallet to view handles.</div>
  }

  useEffect(() => {
    if (!lightbox.open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox({ open: false, url: '', title: '' })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox.open])

  if (loading && items.length === 0) {
    return <div className="text-white/70">Loading...</div>
  }

  if (error && items.length === 0) {
    return <div className="text-red-400 text-sm">{error}</div>
  }

  const renderItem = (it: WalletNFTItem) => {
    const imgUrl = (it.image_url || '').trim()
    const hasPreview = Boolean(imgUrl) && !failedImageIds.has(it.id)
    return (
      <figure key={it.id} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
        <button
          type="button"
          className="block w-full"
          disabled={!hasPreview}
          onClick={() => {
            if (!hasPreview) return
            setLightbox({ open: true, url: imgUrl, title: it.title || it.handle || 'Handle' })
          }}
          aria-label={`Open ${it.title || it.handle || 'handle'} preview`}
        >
          {hasPreview ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={imgUrl}
              alt={it.title}
              className="w-full aspect-video object-cover"
              onError={() => {
                setFailedImageIds((prev) => {
                  if (prev.has(it.id)) return prev
                  const next = new Set(prev)
                  next.add(it.id)
                  return next
                })
              }}
            />
          ) : (
            <div className="w-full aspect-video flex items-center justify-center text-xs text-white/50">
              Preview unavailable
            </div>
          )}
        </button>
        <figcaption className="p-2 text-xs text-white/85 truncate">{it.title || it.handle || 'Handle'}</figcaption>
      </figure>
    )
  }

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <div className="text-white/70 text-sm">No handles yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {items.map(renderItem)}
        </div>
      )}
      {next && (
        <div className="pt-2">
          <button onClick={loadMore} disabled={loading} className="px-3 py-1.5 rounded-lg border border-white/15 text-white/90 hover:bg-white/10 disabled:opacity-60">{loading ? 'Loading...' : 'Load more'}</button>
        </div>
      )}

      {lightbox.open && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox({ open: false, url: '', title: '' })}>
          <div role="dialog" aria-modal="true" className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-white/90 truncate pr-8">{lightbox.title}</div>
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg border border-white/15 text-white/90 hover:bg-white/10"
                onClick={() => setLightbox({ open: false, url: '', title: '' })}
              >
                Close
              </button>
            </div>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={lightbox.url} alt={lightbox.title} className="w-full max-h-[80vh] object-contain rounded-xl border border-white/10 bg-white/5" />
          </div>
        </div>
      )}
    </div>
  )
}
